import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Button,
  TextField,
  Divider,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Alert,
  Tab,
  Tabs,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import {
  Save,
  Refresh,
  Delete,
  Add,
  Security,
  Storage,
  Notifications,
  Api,
} from '@mui/icons-material';
import { AnalyticsService, SettingsService } from '../api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

// Helper to format bytes
const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const Settings: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // System status state
  const [systemStatus, setSystemStatus] = useState({
    storageUsed: 0,
    storageLimit: 10737418240, // 10GB
    storagePercent: 0,
    processingQueue: 0,
    apiStatus: 'healthy' as 'healthy' | 'degraded' | 'down',
    dbStatus: 'healthy' as 'healthy' | 'degraded' | 'down',
  });

  // Settings state
  const [settings, setSettings] = useState({
    autoProcess: true,
    duplicateDetection: true,
    qualityCheck: true,
    notifications: true,
    emailAlerts: false,
    storageLimit: '10',
    retentionDays: '365',
    maxFileSize: '500',
  });

  // Load system status and settings on mount
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Fetch system stats
        const [stats, health] = await Promise.all([
          AnalyticsService.getStats(),
          AnalyticsService.getSystemHealth(),
        ]);

        setSystemStatus({
          storageUsed: stats.storage_used,
          storageLimit: stats.storage_limit,
          storagePercent: Math.round((stats.storage_used / stats.storage_limit) * 100),
          processingQueue: stats.processing_queue,
          apiStatus: health.api_status,
          dbStatus: health.database_status,
        });

        // Try to load saved settings
        try {
          const savedSettings = await SettingsService.getSettings();
          if (savedSettings) {
            setSettings({
              autoProcess: savedSettings.auto_process ?? true,
              duplicateDetection: savedSettings.duplicate_detection ?? true,
              qualityCheck: savedSettings.quality_check ?? true,
              notifications: savedSettings.notifications ?? true,
              emailAlerts: savedSettings.email_alerts ?? false,
              storageLimit: String(Math.round((savedSettings.storage_limit ?? 10737418240) / 1073741824)),
              retentionDays: String(savedSettings.retention_days ?? 365),
              maxFileSize: String(savedSettings.max_file_size ?? 500),
            });
          }
        } catch {
          // Settings API might not exist yet, use defaults
        }
      } catch (error) {
        console.error('Failed to load settings data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSettingChange = (key: string, value: boolean | string) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await SettingsService.updateSettings({
        auto_process: settings.autoProcess,
        duplicate_detection: settings.duplicateDetection,
        quality_check: settings.qualityCheck,
        notifications: settings.notifications,
        email_alerts: settings.emailAlerts,
        storage_limit: parseInt(settings.storageLimit) * 1073741824, // Convert GB to bytes
        retention_days: parseInt(settings.retentionDays),
        max_file_size: parseInt(settings.maxFileSize),
      });
      setSnackbarMessage('Settings saved successfully');
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMessage('Failed to save settings');
      setSnackbarOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const handleResetSettings = () => {
    setSettings({
      autoProcess: true,
      duplicateDetection: true,
      qualityCheck: true,
      notifications: true,
      emailAlerts: false,
      storageLimit: '10',
      retentionDays: '365',
      maxFileSize: '500',
    });
    setSnackbarMessage('Settings reset to defaults');
    setSnackbarOpen(true);
  };

  // API Keys - placeholder for now
  const apiKeys: { name: string; key: string; created: string; lastUsed: string }[] = [];

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
        Settings
      </Typography>

      <Paper sx={{ width: '100%' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{
            borderBottom: 1,
            borderColor: '#333',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
            },
          }}
        >
          <Tab label="General" icon={<Storage />} iconPosition="start" />
          <Tab label="Security" icon={<Security />} iconPosition="start" />
          <Tab label="Notifications" icon={<Notifications />} iconPosition="start" />
          <Tab label="API Keys" icon={<Api />} iconPosition="start" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={8}>
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Processing Settings
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.autoProcess}
                          onChange={(e) => handleSettingChange('autoProcess', e.target.checked)}
                        />
                      }
                      label="Auto-process uploaded files"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.duplicateDetection}
                          onChange={(e) => handleSettingChange('duplicateDetection', e.target.checked)}
                        />
                      }
                      label="Enable duplicate detection"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.qualityCheck}
                          onChange={(e) => handleSettingChange('qualityCheck', e.target.checked)}
                        />
                      }
                      label="Automatic quality analysis"
                    />
                  </Box>
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Storage Settings
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Storage Limit (GB)"
                        value={settings.storageLimit}
                        onChange={(e) => handleSettingChange('storageLimit', e.target.value)}
                        type="number"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Retention Period (days)"
                        value={settings.retentionDays}
                        onChange={(e) => handleSettingChange('retentionDays', e.target.value)}
                        type="number"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Max File Size (MB)"
                        value={settings.maxFileSize}
                        onChange={(e) => handleSettingChange('maxFileSize', e.target.value)}
                        type="number"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} lg={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    System Status
                  </Typography>

                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2">Storage Used</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatBytes(systemStatus.storageUsed)} / {formatBytes(systemStatus.storageLimit)}
                          </Typography>
                        </Box>
                        <Chip
                          label={`${systemStatus.storagePercent}%`}
                          size="small"
                          color={systemStatus.storagePercent > 90 ? 'error' : systemStatus.storagePercent > 70 ? 'warning' : 'primary'}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Processing Queue</Typography>
                        <Chip
                          label={systemStatus.processingQueue === 0 ? 'Empty' : `${systemStatus.processingQueue} items`}
                          size="small"
                          color={systemStatus.processingQueue > 0 ? 'warning' : 'default'}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">API Status</Typography>
                        <Chip
                          label={systemStatus.apiStatus.charAt(0).toUpperCase() + systemStatus.apiStatus.slice(1)}
                          size="small"
                          color={systemStatus.apiStatus === 'healthy' ? 'success' : systemStatus.apiStatus === 'degraded' ? 'warning' : 'error'}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Database Status</Typography>
                        <Chip
                          label={systemStatus.dbStatus.charAt(0).toUpperCase() + systemStatus.dbStatus.slice(1)}
                          size="small"
                          color={systemStatus.dbStatus === 'healthy' ? 'success' : systemStatus.dbStatus === 'degraded' ? 'warning' : 'error'}
                        />
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={8}>
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Access Control
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Session Timeout (minutes)"
                        defaultValue="30"
                        type="number"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Max Login Attempts"
                        defaultValue="5"
                        type="number"
                      />
                    </Grid>
                  </Grid>
                  
                  <Box sx={{ mt: 2 }}>
                    <FormControlLabel
                      control={<Switch defaultChecked />}
                      label="Require two-factor authentication"
                    />
                  </Box>
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Data Protection
                  </Typography>
                  
                  <FormControlLabel
                    control={<Switch defaultChecked />}
                    label="Encrypt files at rest"
                  />
                  <FormControlLabel
                    control={<Switch defaultChecked />}
                    label="Enable audit logging"
                  />
                  <FormControlLabel
                    control={<Switch />}
                    label="Anonymous analytics"
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Notification Preferences
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.notifications}
                          onChange={(e) => handleSettingChange('notifications', e.target.checked)}
                        />
                      }
                      label="In-app notifications"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.emailAlerts}
                          onChange={(e) => handleSettingChange('emailAlerts', e.target.checked)}
                        />
                      }
                      label="Email alerts"
                    />
                    <FormControlLabel
                      control={<Switch />}
                      label="Upload completion notifications"
                    />
                    <FormControlLabel
                      control={<Switch defaultChecked />}
                      label="System maintenance alerts"
                    />
                    <FormControlLabel
                      control={<Switch />}
                      label="Storage limit warnings"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      API Keys
                    </Typography>
                    <Button variant="contained" startIcon={<Add />} disabled>
                      Generate New Key
                    </Button>
                  </Box>

                  <Alert severity="info" sx={{ mb: 2 }}>
                    API key management is coming soon. This feature will allow you to create and manage API keys for programmatic access to the content management system.
                  </Alert>

                  {apiKeys.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Api sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                      <Typography variant="body2" color="text.secondary">
                        No API keys have been created yet.
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        API key management will be available in a future update.
                      </Typography>
                    </Box>
                  ) : (
                    <List>
                      {apiKeys.map((key, index) => (
                        <React.Fragment key={index}>
                          <ListItem>
                            <ListItemText
                              primary={key.name}
                              secondary={
                                <Box>
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 0.5 }}>
                                    {key.key}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Created: {key.created} • Last used: {key.lastUsed}
                                  </Typography>
                                </Box>
                              }
                            />
                            <ListItemSecondaryAction>
                              <IconButton edge="end" sx={{ mr: 1 }}>
                                <Refresh />
                              </IconButton>
                              <IconButton edge="end" color="error">
                                <Delete />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                          {index < apiKeys.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <Box sx={{ p: 3, borderTop: '1px solid #333' }}>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
            sx={{ mr: 2 }}
            onClick={handleSaveSettings}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
          <Button variant="outlined" onClick={handleResetSettings} disabled={saving}>
            Reset to Defaults
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default Settings;